/**
 * Dependency imports
 */
import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import {
  ActionIcon,
  Box,
  Button as MantineButton,
  FileButton,
  Group,
  Stack,
  Text,
  Title,
  Loader,
} from "@mantine/core";

/**
 * Local imports
 */
import Button from "../Button";
import FAIcon from "../FAIcon";
import firestore from "@/firebase/db";
import { uploadFile } from "@/firebase/storage";
import { useCurrentUser } from "@/hooks/use-current-user";

/**
 * Root
 */
const Root = styled("div", {
  shouldForwardProp: (prop: PropertyKey) => ![""].includes(prop.toString()),
})<Partial<DocReviewPanelProps>>((props) => css``);

/**
 * Component(s)
 */

/**
 * DocReviewPanel Component
 */
interface Props {
  onCloseRequest?: () => void;
}

export type DocReviewPanelProps = Props &
  React.PropsWithRef<React.ComponentProps<"div">>;

const DocReviewPanel: React.FC<DocReviewPanelProps> = ({
  onCloseRequest,
  ...props
}) => {
  const [done, setDone] = React.useState(false);
  const [uploading, setUploading] = React.useState(-1);
  const [files, setFiles] = React.useState<File[] | null>(null);
  const [currentUser, authStatus] = useCurrentUser();

  const maxFileCount = 20;
  const maxFileSize = 25 * 1024 * 1024; // in bytes
  const maxFileSizeString = (maxFileSize / (1024 * 1024)).toFixed(1) + " MB";
  const filesTooBig = files?.some((file) => file.size > maxFileSize);

  const uploadFiles = async () => {
    setDone(false);

    setUploading(0);

    if (authStatus === "logged-in") {
      if (!files || files.length === 0) return;

      const doc = await firestore.create("Doc_Reviews", {
        data: {
          info: {
            status: "pending",
            created: new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            }),
          },
          client: {
            id: currentUser?.uid,
            email: currentUser?.email,
          },
          files: files.map((file) => file.name),
        },
      });

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > maxFileSize) continue;
        setUploading(i);
        await uploadFile(
          `documents/${currentUser?.uid}/${doc.id}/files/${file.name}`,
          file
        );
      }

      await firestore.update(`Doc_Reviews/${doc.id}`, {
        id: doc.id,
      });

      setUploading(Number.POSITIVE_INFINITY);
      setDone(true);
    }
  };

  return (
    <Root {...props} style={{ overflow: "auto", height: "100%" }}>
      <Box p={30}>
        <Title size={32}>
          {done
            ? "Thank you"
            : uploading > -1
              ? "Uploading..."
              : "Document Review"}
        </Title>

        {done ? (
          <Text opacity={0.7} fz={14} mt={5}>
            We received your request. We will review the documents and get back
            to you soon.
          </Text>
        ) : uploading > -1 ? (
          <Text opacity={0.7} fz={14} mt={5}>
            Please wait while we upload the documents.
          </Text>
        ) : (
          <Text opacity={0.7} fz={14} mt={5}>
            Please upload the document(s) you would like reviewed.
          </Text>
        )}

        <Box
          mt={20}
          p={20}
          style={{
            border: "1px dashed rgba(0, 0, 0, 0.1)",
            borderRadius: 15,
          }}
        >
          <div>
            {files && files.length > 0 && (
              <Stack gap={5} style={{ maxHeight: "50vh", overflowY: "auto" }}>
                {files.map((file, index) => (
                  <Group
                    key={index}
                    p={10}
                    gap={5}
                    bg={
                      file.size > maxFileSize
                        ? "rgba(255, 0, 0, 0.1)"
                        : "rgba(0, 0, 0, 0.05)"
                    }
                    wrap="nowrap"
                    style={{
                      borderRadius: 5,
                    }}
                  >
                    <Text
                      size="sm"
                      mr={5}
                      truncate
                      c={file.size > maxFileSize ? "red" : "inherit"}
                    >
                      {file.name}
                    </Text>

                    <Text
                      ml="auto"
                      fz={10}
                      fw="bold"
                      tt="uppercase"
                      c={file.size > maxFileSize ? "red" : "#666"}
                      bg={
                        file.size > maxFileSize
                          ? "rgba(255, 0, 0, 0.15)"
                          : "rgba(0, 0, 0, 0.1)"
                      }
                      px={4}
                      py={1}
                      style={{ borderRadius: 3, whiteSpace: "nowrap" }}
                    >
                      {file.size < 1024 * 1024
                        ? (file.size / 1024).toFixed(1) + " KB"
                        : (file.size / (1024 * 1024)).toFixed(1) + " MB"}
                    </Text>

                    <Text
                      fz={10}
                      fw="bold"
                      tt="uppercase"
                      c={file.size > maxFileSize ? "red" : "#666"}
                      bg={
                        file.size > maxFileSize
                          ? "rgba(255, 0, 0, 0.15)"
                          : "rgba(0, 0, 0, 0.1)"
                      }
                      px={4}
                      py={1}
                      style={{ borderRadius: 3 }}
                    >
                      {file.type.split("/")[1]}
                    </Text>

                    <ActionIcon
                      variant="subtle"
                      color={file.size > maxFileSize ? "red" : "gray"}
                      size="xs"
                      disabled={uploading > -1}
                      onClick={() => {
                        const newFiles = [...files];
                        newFiles.splice(index, 1);
                        setFiles(newFiles);
                      }}
                    >
                      {uploading === -1 ? (
                        <FAIcon name="xmark" />
                      ) : uploading > index ? (
                        <FAIcon
                          name="check"
                          c={uploading > index ? "green" : "inherit"}
                        />
                      ) : (
                        <Loader size={12} color="gray" />
                      )}
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
            )}

            {(!files || files.length === 0) && (
              <Stack align="center" gap={10}>
                <FileButton
                  onChange={(selectedFiles) =>
                    setFiles([...(files ?? []), ...selectedFiles])
                  }
                  multiple
                >
                  {(props) => (
                    <Button
                      {...props}
                      size="small"
                      transition={false}
                      expandOnHover={false}
                      component="button"
                    >
                      Upload Files
                    </Button>
                  )}
                </FileButton>
              </Stack>
            )}
          </div>
        </Box>

        {!done &&
          uploading === -1 &&
          (files && files.length > maxFileCount ? (
            <Text fz={10} c="red" mt={5} ta="center">
              Please only select up to <strong>{maxFileCount}</strong> files. (
              {files.length} selected)
            </Text>
          ) : filesTooBig ? (
            <Text fz={10} c="red" mt={5} ta="center">
              Some files are too big. Max file size is {maxFileSizeString}.
            </Text>
          ) : (
            <Text fz={10} opacity={0.5} mt={5} ta="center">
              Select up to {maxFileCount} files. Max file size is{" "}
              {maxFileSizeString}
            </Text>
          ))}

        {!done && uploading === -1 && files && files.length > 0 && (
          <Group mt={30} justify="center">
            <FileButton
              onChange={(selectedFiles) =>
                setFiles([...(files ?? []), ...selectedFiles])
              }
              multiple
            >
              {(props) => (
                <MantineButton
                  {...props}
                  variant="light"
                  size="sm"
                  color="gray"
                  radius={999}
                >
                  <Text fz={11}>Upload more files</Text>
                </MantineButton>
              )}
            </FileButton>

            <Text fz={11} opacity={0.5} mx={10}>
              OR
            </Text>

            <Button
              size="small"
              hasArrow
              transition={false}
              onClick={uploadFiles}
              expandOnHover={false}
              component='button'
            >
              Continue
            </Button>
          </Group>
        )}
      </Box>
    </Root>
  );
};

export default DocReviewPanel;
